import { create } from "zustand"
import { persist } from "zustand/middleware"

import { Experiment } from "../constants/experiments"

interface ExperimentState {
  experiments: Experiment[]
  addExperiment: (experiment: Experiment) => void
  removeExperiment: (experiment: Experiment) => void
  getExperiments: () => Experiment[]
  getAvailableExperiments: () => Experiment[]
}

export const useExperimentStore = create<ExperimentState>()(
  persist(
    (set, get) => ({
      experiments: [],
      addExperiment: (experiment: Experiment) =>
        set(({ experiments }) => ({
          // Prevent duplicates
          experiments: (experiments.includes(experiment)
            ? experiments
            : [...experiments, experiment]
          ).filter(
            // clear out any old experiments
            (experiment) => Object.values(Experiment).includes(experiment)
          ),
        })),
      removeExperiment: (experiment: Experiment) =>
        set(({ experiments }) => ({
          experiments: experiments.filter((e) => e !== experiment),
        })),
      getExperiments: () => get().experiments,
      getAvailableExperiments: () => Object.values(Experiment),
    }),
    {
      name: "experiments",
      partialize: (state) => ({ experiments: state.experiments }),
    }
  )
)

export const useIsExperiment = (experiment: Experiment) => {
  const experiments = useExperimentStore((state) => state.experiments)
  return experiments.includes(experiment)
}

/**
 * This should be called once from the browser entry point
 */
export const initializeExperiments = () => {
  const {
    addExperiment,
    removeExperiment,
    getExperiments,
    getAvailableExperiments,
  } = useExperimentStore.getState()

  window.experiments = {
    addExperiment,
    removeExperiment,
    getExperiments,
    getAvailableExperiments,
  }
}
